<script setup>
// "When & where" on the ladder challenge page (spec R4). The fixture page keeps
// FixtureWhenAndWhere; a challenge needs more: before acceptance there is no
// match, only the challenger's suggestion, and afterwards the card says who
// proposed a time and who has to confirm it. Every word comes from
// utils/challengeSchedule.js (the app has the same states and copy); this only
// lays it out and offers the time actions (Confirm time / Suggest a time /
// Change time / Reschedule, and the challenger's Change suggested time while
// waiting). Accept, decline, cancel, result and live scoring
// live with the page's other actions.
import {computed} from 'vue';

const props = defineProps({
  // challengeSchedule(...) output
  schedule: {type: Object, required: true},
  confirmPending: {type: Boolean, default: false},
  headClass: {type: String, default: ''},
  cardClass: {type: String, default: ''},
});
const emit = defineEmits(['action']);

const {outputDateFromDb} = useCrudConverters();

const title = computed(() => {
  const s = props.schedule;
  if (s.state === 'S7') return s.playedAt ? `Played ${outputDateFromDb(s.playedAt, 'ccc d MMM, HH:mm')}` : 'Played';
  if (s.when) return outputDateFromDb(s.when, 'ccc d MMM, HH:mm');
  return s.emptyText;
});

const timeActions = computed(() =>
    (props.schedule.actions ?? []).filter(a => ['confirmTime', 'schedule', 'changeSuggestion'].includes(a.id)));
// Filled: the one-tap confirm, and a passed suggestion's "Suggest a new time".
const isPrimary = (a) => a.id === 'confirmTime' || a.primary === true;
</script>

<template>
  <template v-if="schedule.show">
    <div class="tl-section-head" :class="headClass"><h2>When &amp; where</h2></div>
    <div class="tl-card" :class="cardClass">
      <div class="cww-row">
        <div class="cww-icon">
          <v-icon size="17" color="charcoal">{{ schedule.state === 'S7' ? 'mdi-calendar-check' : 'mdi-calendar-clock' }}</v-icon>
        </div>
        <div class="flex-grow-1 min-w-0">
          <div class="cww-title" :class="{'cww-title--passed': schedule.whenPassed, 'cww-title--empty': !schedule.when && schedule.state !== 'S7'}">
            {{ title }}
          </div>
          <div v-if="schedule.line" class="cww-sub">{{ schedule.line }}</div>
          <div v-if="schedule.changedHint" class="cww-changed">
            <v-icon size="13" class="mr-1">mdi-update</v-icon>{{ schedule.changedHint }}
          </div>
        </div>
        <v-chip v-if="schedule.chip" variant="flat" size="small"
                :class="['tl-chip', 'cww-chip', `tl-chip--${schedule.chip.kind === 'muted' ? 'neutral' : schedule.chip.kind}`]">
          {{ schedule.chip.label }}
        </v-chip>
      </div>
      <template v-if="schedule.area">
        <div class="cww-divider"></div>
        <div class="cww-row">
          <div class="cww-icon">
            <v-icon size="17" color="charcoal">mdi-map-marker-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="cww-title" :class="{'cww-title--passed': schedule.whenPassed}">{{ schedule.area }}</div>
            <div class="cww-sub">Playing area</div>
          </div>
        </div>
      </template>
      <div v-if="timeActions.length" class="cww-actions">
        <v-btn
            v-for="a in timeActions"
            :key="a.id + a.label"
            color="primary"
            :variant="isPrimary(a) ? 'flat' : 'outlined'"
            :prepend-icon="a.id === 'confirmTime' ? 'mdi-check' : 'mdi-calendar-edit'"
            :loading="a.id === 'confirmTime' && confirmPending"
            :disabled="a.id !== 'confirmTime' && confirmPending"
            @click="emit('action', a)"
        >{{ a.label }}</v-btn>
      </div>
    </div>
  </template>
</template>

<style scoped>
/* Same hairline card rhythm as FixtureWhenAndWhere */
.cww-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.cww-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.min-w-0 {
  min-width: 0;
}

/* the chip keeps its whole word at 360px; the text beside it wraps instead */
.cww-chip {
  flex-shrink: 0;
  max-width: none;
  overflow: visible;
}

.cww-chip :deep(.v-chip__content) {
  overflow: visible;
  white-space: nowrap;
}

.cww-title {
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.cww-title--passed {
  color: rgb(var(--v-theme-textSubtle));
  text-decoration: line-through;
}

.cww-title--empty {
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 500;
}

.cww-sub {
  font-size: 11.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

.cww-changed {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-warning));
}

.cww-divider {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.cww-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

/* long labels ("Change Sam Smith's suggested time") wrap at phone width
   instead of running out of the button */
.cww-actions .v-btn {
  max-width: 100%;
  height: auto !important;
  min-height: 36px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.cww-actions .v-btn :deep(.v-btn__content) {
  white-space: normal;
  text-align: left;
  line-height: 1.3;
}
</style>
